import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useGetPersonnel } from '../../../../../../traits/api/workRegisters/useGetPersonnel';
import { useEditPersonnelForm } from '../../hooks/useEditPersonnelForm';
import { Input } from '../Input';
import { RadioInput } from '../RadioInput';
import { Controller } from 'react-hook-form';
import { useControl } from '../../../../components/ControlLayout';
import { useGetPersonnelFiles } from '../../../../../../traits/api/workRegisters/useGetPersonnelFiles';
import { useGetPersonnelID06Competences } from '../../../../../../traits/api/workRegisters/useGetPersonnelID06Competences';
import { Loading } from '../../../../../../components/Loading';
import { Error } from '../../../../../../components/Error';
import { toast } from '../../../../../../components/Toast';
import { useDeletePersonnelFile } from '../../../../../../traits/api/workRegisters/useDeleteFile';
import { useUpsertQuestionnaire } from '../../../../../../traits/api/workRegisters/useUpsertQuestionnaire';
import { useUpdateFileComment } from '../../../../../../traits/api/workRegisters/useUpdateFileComment';
import { useUpdateID06Comment } from '../../../../../../traits/api/workRegisters/useUpdateID06Comment';
import { useUpdatePersonnel } from '../../../../../../traits/api/workRegisters/useUpdatePersonnel';
import { usePostPersonnelFile } from '../../../../../../traits/api/workRegisters/usePostPersonnelFile';
import { format } from 'date-fns';

import { S } from './EditPersonnelModal.styles';
import { UpsertPersonnelQuestionnaireDtoTypeOfIdEnum } from '@ansvar-sakerhet/api-client';

type Props = {
  personnelId: string;
  onCancel: () => void;
  /** Left panel = person data, right panel = questionnaire */
  section?: 'personnel' | 'questionnaire';
};

type PendingPhoto = {
  id: string;
  file: File;
  previewUrl: string;
};

const toDateTimeLocal = (value?: string | Date | null) => {
  if (!value) return '';
  try {
    return format(new Date(value), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};

export const EditPersonnelModal: React.FC<Props> = (props) => {
  const section = props.section ?? 'questionnaire';
  const isPersonnelSection = section === 'personnel';
  const isQuestionnaireSection = section === 'questionnaire';

  const { data } = useControl();
  const personnel = useGetPersonnel(props.personnelId, data.controlId);
  const files = useGetPersonnelFiles(props.personnelId, data.controlId);
  const deleteFile = useDeletePersonnelFile(props.personnelId, data.controlId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);

  const competences = useGetPersonnelID06Competences(
    props.personnelId,
    data.controlId
  );

  const updateQuestionnaire = useUpsertQuestionnaire(
    props.personnelId,
    data.controlId
  );

  const updateFileComment = useUpdateFileComment(
    props.personnelId,
    data.controlId
  );

  const updateID06Comment = useUpdateID06Comment(
    props.personnelId,
    data.controlId
  );

  const updatePersonnel = useUpdatePersonnel(props.personnelId, data.controlId);

  const postPersonnelFile = usePostPersonnelFile(
    props.personnelId,
    data.controlId
  );

  const { handleSubmit, setValue, watch, register, control, getValues, reset } =
    useEditPersonnelForm(section);

  const activeFiles = files.data?.filter(
    (file) => !watch('deletedFiles')?.includes(file.personnelFileId)
  );

  const refetchData = () => {
    personnel.refetch();
    files.refetch();
    competences.refetch();
  };

  const onErrors = () => {
    toast.error('Formuläret är inte korrekt ifyllt');
  };

  const clearPendingPhotos = () => {
    setPendingPhotos((current) => {
      current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
      return [];
    });
  };

  const handleAddPhotos = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files;
    if (!selected?.length) return;

    const next = Array.from(selected).map((file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setPendingPhotos((current) => [...current, ...next]);
    event.target.value = '';
  };

  const removePendingPhoto = (id: string) => {
    setPendingPhotos((current) => {
      const photo = current.find((item) => item.id === id);
      if (photo) URL.revokeObjectURL(photo.previewUrl);
      return current.filter((item) => item.id !== id);
    });
  };

  const handleSave = async () => {
    const {
      firstName,
      lastName,
      arrivedAt,
      id06Comment,
      hasIdCard,
      hasSafetyEquipment,
      canShowValidA1Certificate,
      canShowValidWorkPermit,
      hasSignsOfIntoxication,
      questionnaireComment,
      deletedFiles,
      files: fileComments,
      typeOfId,
      newFileComments,
    } = watch();

    try {
      if (isPersonnelSection) {
        await updatePersonnel.mutateAsync({
          firstName,
          lastName,
          arrivedAt: arrivedAt ? new Date(arrivedAt).toISOString() : null,
        });

        if (
          personnel.data?.id06Card &&
          id06Comment !== personnel.data.id06Card.comment
        ) {
          await updateID06Comment.mutateAsync({
            comment: id06Comment,
          });
        }

        if (deletedFiles?.length) {
          await Promise.all(
            deletedFiles.map((fileId) =>
              deleteFile.mutateAsync({ personnelFileId: fileId })
            )
          );
        }

        if (fileComments?.length) {
          await Promise.all(
            fileComments.map((file) =>
              updateFileComment.mutateAsync({
                personnelFileId: file.personnelFileId,
                data: {
                  comment: file.comment,
                },
              })
            )
          );
        }

        if (pendingPhotos.length) {
          await Promise.all(
            pendingPhotos.map((photo) =>
              postPersonnelFile.mutateAsync({
                file: photo.file,
                comment: newFileComments?.[photo.id],
              })
            )
          );
        }
      }

      if (isQuestionnaireSection) {
        await updateQuestionnaire.mutateAsync({
          hasIdCard: hasIdCard ?? undefined,
          hasSafetyEquipment: hasSafetyEquipment ?? undefined,
          canShowValidA1Certificate: canShowValidA1Certificate ?? undefined,
          canShowValidWorkPermit: canShowValidWorkPermit ?? undefined,
          hasSignsOfIntoxication: hasSignsOfIntoxication ?? undefined,
          typeOfId: typeOfId ?? undefined,
          comment: questionnaireComment ?? undefined,
        });
      }

      clearPendingPhotos();
      toast.success('Ändringarna har sparats');
      props.onCancel();
    } catch (e) {
      toast.error('Något gick fel, gick inte att spara ändringarna');
    }
  };

  useEffect(() => {
    if (!personnel.data) return;

    const { questionnaire, id06Card } = personnel.data;

    const filesComment = files.data?.map((file) => ({
      personnelFileId: file.personnelFileId,
      comment: file.comment ?? '',
    }));

    reset({
      firstName: personnel.data.firstName,
      lastName: personnel.data.lastName,
      arrivedAt: toDateTimeLocal(personnel.data.arrivedAt),
      deletedFiles: [],
      files: filesComment ?? [],
      newFileComments: {},
      id06Comment: id06Card?.comment ?? '',
      hasIdCard: questionnaire?.hasIdCard ?? undefined,
      hasSafetyEquipment: questionnaire?.hasSafetyEquipment ?? undefined,
      canShowValidA1Certificate:
        questionnaire?.canShowValidA1Certificate ?? undefined,
      canShowValidWorkPermit:
        questionnaire?.canShowValidWorkPermit ?? undefined,
      hasSignsOfIntoxication:
        questionnaire?.hasSignsOfIntoxication ?? undefined,
      questionnaireComment: questionnaire?.comment ?? undefined,
      typeOfId: questionnaire?.typeOfId ?? undefined,
    });
  }, [personnel.data, files.data, competences.data]);

  useEffect(() => {
    return () => clearPendingPhotos();
  }, []);

  return (
    <Dialog.Portal>
      <S.Overlay>
        <S.Content>
          <S.ContentHeader>
            <Dialog.Title asChild>
              <S.H4>
                Redigera{' '}
                {personnel.data?.firstName + ' ' + personnel.data?.lastName}
              </S.H4>
            </Dialog.Title>

            <S.CloseButton asChild onClick={props.onCancel}>
              <S.Icon src="Close" width={20} height={20} />
            </S.CloseButton>
          </S.ContentHeader>
          {(personnel.isLoading ||
            files.isLoading ||
            competences.isLoading) && <Loading />}

          {(personnel.isError || files.isError || competences.isError) && (
            <Error refetch={refetchData} />
          )}

          {personnel.isSuccess && files.isSuccess && competences.isSuccess && (
            <form onSubmit={handleSubmit(handleSave, onErrors)}>
              {isPersonnelSection && (
                <>
                  <S.FieldSet>
                    <S.H5>Personuppgifter</S.H5>
                    <S.FlexView>
                      <S.Grid rows={2}>
                        <Input label="Förnamn" {...register('firstName')} />
                        <Input label="Efternamn" {...register('lastName')} />
                        <Input
                          label="Ankomst"
                          type="datetime-local"
                          {...register('arrivedAt')}
                        />
                        <Input
                          label="Personnummer"
                          value={personnel.data.personalCodeNumber ?? '-'}
                          disabled
                          readOnly
                        />
                      </S.Grid>
                      <S.B3 $color="darkGray">
                        Status, nationalitet, ID06-giltighet och utbildningar
                        hämtas från ID06 och kan inte redigeras här.
                      </S.B3>
                    </S.FlexView>
                  </S.FieldSet>

                  <S.FieldSet>
                    <S.H5>Anteckningar</S.H5>
                    <S.FlexView>
                      {personnel.data.id06Card ? (
                        <Input
                          label="Anteckning"
                          {...register('id06Comment')}
                        />
                      ) : (
                        <S.B3 $color="darkGray">
                          Personen saknar ID06-kort, så anteckningar kan inte
                          sparas ännu. Du kan fortfarande lägga till foton
                          nedan.
                        </S.B3>
                      )}
                    </S.FlexView>
                  </S.FieldSet>

                  <S.FieldSet>
                    <S.H5>Foton</S.H5>
                    <S.FlexView>
                      <S.UploadRow>
                        <S.Button
                          type="button"
                          $outlined
                          $height={38}
                          $width={180}
                          $fontSize={14}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Lägg till foto
                        </S.Button>
                        <S.HiddenFileInput
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleAddPhotos}
                        />
                      </S.UploadRow>

                      {pendingPhotos.map((photo) => (
                        <S.ImageContainer key={photo.id}>
                          <S.RelativeContainer>
                            <S.SmallText>Ny bild</S.SmallText>
                            <S.Image src={photo.previewUrl} />
                            <S.DeleteButton
                              type="button"
                              onClick={() => removePendingPhoto(photo.id)}
                            >
                              <S.Icon
                                src="Close"
                                color="white"
                                width={22}
                                height={22}
                              />
                            </S.DeleteButton>
                          </S.RelativeContainer>
                          <S.TextInputWrapper>
                            <S.B3>Kommentar</S.B3>
                            <S.TextInput
                              value={watch('newFileComments')?.[photo.id] ?? ''}
                              onChange={(e) => {
                                setValue('newFileComments', {
                                  ...getValues('newFileComments'),
                                  [photo.id]: e.target.value,
                                });
                              }}
                            />
                          </S.TextInputWrapper>
                        </S.ImageContainer>
                      ))}

                      {activeFiles?.map((file) => (
                        <Controller
                          key={file.personnelFileId}
                          name="files"
                          control={control}
                          render={({ field }) => (
                            <S.ImageContainer>
                              <S.RelativeContainer>
                                <S.SmallText>Yrkesarbetare</S.SmallText>
                                <S.Image src={file.file.url} />
                                <S.DeleteButton
                                  id={file.personnelFileId}
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setValue('deletedFiles', [
                                      ...getValues('deletedFiles'),
                                      e.currentTarget.id,
                                    ]);
                                    setValue(
                                      'files',
                                      getValues('files').filter(
                                        (value) =>
                                          value.personnelFileId !==
                                          e.currentTarget.id
                                      )
                                    );
                                  }}
                                >
                                  <S.Icon
                                    src="Close"
                                    color="white"
                                    width={22}
                                    height={22}
                                  />
                                </S.DeleteButton>
                              </S.RelativeContainer>
                              <S.TextInputWrapper>
                                <S.B3>Kommentar</S.B3>
                                <S.TextInput
                                  value={
                                    field.value?.find(
                                      (value) =>
                                        value.personnelFileId ===
                                        file.personnelFileId
                                    )?.comment ?? ''
                                  }
                                  onChange={(e) => {
                                    setValue(
                                      'files',
                                      field.value.map((value) =>
                                        value.personnelFileId ===
                                        file.personnelFileId
                                          ? {
                                              ...value,
                                              comment: e.target.value,
                                            }
                                          : value
                                      )
                                    );
                                  }}
                                />
                              </S.TextInputWrapper>
                            </S.ImageContainer>
                          )}
                        />
                      ))}

                      {!activeFiles?.length && !pendingPhotos.length && (
                        <S.B3 $color="darkGray">
                          Inga foton ännu. Lägg till ett foto ovan.
                        </S.B3>
                      )}
                    </S.FlexView>
                  </S.FieldSet>
                </>
              )}

              {isQuestionnaireSection && (
                <S.FieldSet>
                  <S.H5>Frågeformulär</S.H5>

                  <RadioInput
                    state={watch('hasIdCard') ?? undefined}
                    question="Finns ID handling?"
                    control={control}
                    name="hasIdCard"
                    options={[
                      { label: 'Ja', value: 'Yes' },
                      { label: 'Nej', value: 'No' },
                      { label: 'Oklart', value: 'Unclear' },
                    ]}
                  />

                  <RadioInput
                    state={watch('typeOfId') ?? undefined}
                    question="Typ av ID handling"
                    control={control}
                    name="typeOfId"
                    options={[
                      {
                        label: 'Pass',
                        value:
                          UpsertPersonnelQuestionnaireDtoTypeOfIdEnum.Passport,
                      },
                      {
                        label: 'Nationellt',
                        value:
                          UpsertPersonnelQuestionnaireDtoTypeOfIdEnum.NationalId,
                      },
                      {
                        label: 'Körkort',
                        value:
                          UpsertPersonnelQuestionnaireDtoTypeOfIdEnum.DriverLicense,
                      },
                      {
                        label: 'SKV Id',
                        value:
                          UpsertPersonnelQuestionnaireDtoTypeOfIdEnum.Skvid,
                      },
                      {
                        label: 'Digitalt ID',
                        value:
                          UpsertPersonnelQuestionnaireDtoTypeOfIdEnum.DigitalId,
                      },
                    ]}
                  />

                  <RadioInput
                    state={watch('hasSafetyEquipment') ?? undefined}
                    question="Är personlig säkerhetsutrustning ok?"
                    control={control}
                    name="hasSafetyEquipment"
                    options={[
                      { label: 'Ja', value: 'Yes' },
                      { label: 'Nej', value: 'No' },
                    ]}
                  />

                  <RadioInput
                    state={watch('canShowValidA1Certificate') ?? undefined}
                    question="Kan uppvisa giltigt A1-intyg?"
                    control={control}
                    name="canShowValidA1Certificate"
                    options={[
                      { label: 'Ja', value: 'Yes' },
                      { label: 'Nej', value: 'No' },
                      { label: 'Oklart', value: 'Unclear' },
                      { label: 'Behövs ej', value: 'NotRequired' },
                    ]}
                  />

                  <RadioInput
                    state={watch('canShowValidWorkPermit') ?? undefined}
                    question="Kan uppvisa giltigt arbetstillstånd?"
                    control={control}
                    name="canShowValidWorkPermit"
                    options={[
                      { label: 'Ja', value: 'Yes' },
                      { label: 'Nej', value: 'No' },
                      { label: 'Oklart', value: 'Unclear' },
                      { label: 'Behövs ej', value: 'NotRequired' },
                    ]}
                  />

                  <RadioInput
                    state={watch('hasSignsOfIntoxication') ?? undefined}
                    question="Finns tecken på onykterhet/drogpåverkan?"
                    control={control}
                    name="hasSignsOfIntoxication"
                    options={[
                      { label: 'Ja', value: 'Yes' },
                      { label: 'Nej', value: 'No' },
                      { label: 'Oklart', value: 'Unclear' },
                    ]}
                  />

                  <Input
                    label="Kommentar"
                    {...register('questionnaireComment')}
                  />
                </S.FieldSet>
              )}

              <S.ContentFooter>
                <Dialog.Close asChild onClick={props.onCancel}>
                  <S.Button $outlined>Avbryt</S.Button>
                </Dialog.Close>

                <S.Button type="submit">Spara</S.Button>
              </S.ContentFooter>
            </form>
          )}
        </S.Content>
      </S.Overlay>
    </Dialog.Portal>
  );
};
